import type { HqlCatalog, HqlCompileResult } from '@rock/hql';
import { hqlReferences } from '@rock/hql';
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * What a query reads, as links into the catalog: the equipment types it names, the info types it asks
 * about, the brands it compares, and the spaces a home must complete before it's evaluated. Shown even
 * when the query no longer compiles, as long as it parses.
 */
export const QueryReferences: FC<{ result: HqlCompileResult; catalog: HqlCatalog }> = ({ result, catalog }) => {
    if (!result.ast) return null;
    const refs = hqlReferences(result.ast);
    const compiled = result.compiled;
    const infoSpace = (id: string) => catalog.infoTypes.find((type) => type.id === id)?.spaceId;
    return (
        <div className="grid gap-3 md:grid-cols-2">
            <Chips label="Equipment types" empty={refs.coversEquipment ? 'Any type' : 'None (whole home)'}>
                {refs.equipmentTypeIds.map((id) => (
                    <Chip key={id} to={`/equipment/types/${id}`} tone="bg-green-100 text-green-800">
                        {id}
                    </Chip>
                ))}
            </Chips>
            <Chips label="Info types" empty="None">
                {refs.infoTypeIds.map((id) => (
                    <Chip
                        key={id}
                        to={infoSpace(id) ? `/spaces/${infoSpace(id)}/info` : undefined}
                        tone="bg-blue-100 text-blue-800"
                    >
                        {id}
                    </Chip>
                ))}
            </Chips>
            <Chips label="Waits for spaces" empty="None">
                {(compiled?.requiredSpaceIds ?? []).map((id) => (
                    <Chip key={id} to={`/spaces/${id}`} tone="bg-purple-100 text-purple-800">
                        {id}
                    </Chip>
                ))}
            </Chips>
            <Chips label="Brands" empty="None">
                {refs.brands.map((name) => (
                    <Chip key={name} tone="bg-slate-100 text-slate-800">
                        {name}
                    </Chip>
                ))}
            </Chips>
        </div>
    );
};

const Chips: FC<{ label: string; empty: string; children: ReactNode[] }> = ({ label, empty, children }) => (
    <div className="flex flex-col gap-1">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="flex flex-wrap gap-1">
            {children.length > 0 ? children : <span className="text-sm text-muted-foreground">{empty}</span>}
        </div>
    </div>
);

const Chip: FC<{ to?: string; tone: string; children: ReactNode }> = ({ to, tone, children }) => {
    const className = `inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${tone}`;
    return to ? (
        <Link to={to} className={`${className} hover:underline`}>
            {children}
        </Link>
    ) : (
        <span className={className}>{children}</span>
    );
};
